import type { Texture, TextureLoader } from 'three';
import type { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';

import type { Ktx2Support } from './ktx2-support';
import { loadWithStallRetry } from './load-with-stall-retry.helper';

export type LoadWithKtx2FallbackParams = {
	originalUrl: string;
	ktx2Support: Ktx2Support;
	textureLoader: TextureLoader;
	hdrLoader: RGBELoader;
	onLoad: (texture: Texture) => void;
	onError?: (error: unknown) => void;
};

/**
 * Attempt to load a KTX2 variant of `originalUrl` (same path, extension swapped
 * to `.ktx2`). Supported source extensions: `.jpg`, `.jpeg`, `.png`. `.hdr` is
 * temporarily excluded from the KTX2-first path and always loads via the
 * original URL (re-enable later when KTX2-HDR assets are deployed).
 * On any KTX2 error — including 404 — fall back to the original URL via
 * `TextureLoader` for `.jpg`/`.jpeg`/`.png`. `.hdr` URLs skip the KTX2
 * attempt entirely and load directly via `RGBELoader`.
 *
 * KTX2 textures stay GPU-compressed, cutting VRAM ~2–8× vs. decoded RGBA8 —
 * the mobile memory budget win that Phase 1 (renderer overrides) couldn't
 * reach on its own. This helper lets existing JPG/PNG deployments opt in
 * progressively: hosts that add `.ktx2` siblings get the savings; those that
 * don't get the original file unchanged.
 */
export const loadWithKtx2Fallback = (params: LoadWithKtx2FallbackParams): void => {
	const { originalUrl, ktx2Support, textureLoader, hdrLoader, onLoad, onError } = params;

	const isHdr = /\.hdr($|\?)/i.test(originalUrl);
	const fallbackLoader: TextureLoader | RGBELoader = isHdr ? hdrLoader : textureLoader;

	const loadOriginal = (): void => {
		loadWithStallRetry({
			url: originalUrl,
			label: 'texture-fallback',
			load: (url, ol, oe) => fallbackLoader.load(url, ol, undefined, oe),
			onLoad,
			onError: (err) => onError?.(err)
		});
	};

	const ktx2Loader = ktx2Support.isEnabled ? ktx2Support.loader : null;
	const ktx2Url = ktx2Loader ? toKtx2Url(originalUrl) : null;
	if (!ktx2Loader || ktx2Url === null) {
		loadOriginal();
		return;
	}

	loadWithStallRetry({
		url: ktx2Url,
		label: 'texture-ktx2',
		load: (url, ol, oe) => ktx2Loader.load(url, ol, undefined, oe),
		onLoad,
		onError: () => loadOriginal()
	});
};

const toKtx2Url = (url: string): string | null => {
	// Preserve query string / hash when swapping extension.
	// NOTE: .hdr intentionally omitted — KTX2-HDR not yet deployed.
	const match = url.match(/^(.*)\.(jpg|jpeg|png)(\?.*|#.*)?$/i);
	if (!match) return null;
	const [, stem, , suffix = ''] = match;
	return `${stem}.ktx2${suffix}`;
};
